/* Windlust house emblem. Official mesh, reflected studio lighting, visibility-aware interactive WebGL. */
(() => {
  if (customElements.get('windlust-emblem')) return;
  const VERTEX = `
    attribute vec3 aPosition; attribute vec3 aNormal;
    uniform mat3 uRotation; uniform float uAspect;
    varying vec3 vPosition; varying vec3 vNormal;
    void main() {
      vec3 p = uRotation * aPosition;
      vec3 n = aNormal;
      // A subtly crowned surface breaks up reflections like a polished cast emblem.
      float crown = smoothstep(0.70, 1.0, abs(n.z));
      n = normalize(n + vec3(aPosition.x * 0.30, aPosition.y * 0.20, 0.0) * crown);
      vPosition = p; vNormal = uRotation * n;
      gl_Position = vec4(p.x * 3.26 / uAspect, p.y * 3.26, (2.3-p.z)*0.5, 4.0-p.z);
    }`;
  const FRAGMENT = `
    #ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    #else
    precision mediump float;
    #endif
    varying vec3 vPosition; varying vec3 vNormal;
    float lightPanel(vec3 r, vec3 direction, float width, float height) {
      vec3 axis = normalize(cross(vec3(0.0, 1.0, 0.0), direction));
      vec3 up = cross(direction, axis);
      float facing = max(dot(r, direction), 0.0);
      vec2 uv = vec2(dot(r, axis), dot(r, up));
      return exp(-pow(uv.x / width, 2.0) - pow(uv.y / height, 2.0)) * pow(facing, 3.0);
    }
    vec3 studio(vec3 r) {
      // Fixed studio softboxes are reflected by the turning metal, rather than painted onto it.
      vec3 light = vec3(0.040, 0.035, 0.028) + vec3(0.08, 0.085, 0.09) * (r.y * 0.5 + 0.5);
      light += vec3(1.03, 1.07, 1.14) * lightPanel(r, normalize(vec3(-0.52, 0.28, 1.0)), 0.13, 0.88) * 1.15;
      light += vec3(1.25, 1.08, 0.78) * lightPanel(r, normalize(vec3(0.72, 0.03, 0.8)), 0.055, 0.92) * 2.8;
      light += vec3(1.12, 1.05, 0.88) * lightPanel(r, normalize(vec3(0.0, 0.95, 0.40)), 0.70, 0.08) * 1.8;
      light += vec3(0.85, 0.94, 1.15) * lightPanel(r, normalize(vec3(-0.75, 0.1, -0.75)), 0.10, 0.85) * 1.65;
      light += vec3(1.15, 0.95, 0.61) * lightPanel(r, normalize(vec3(0.4, 0.55, -0.95)), 0.23, 0.82) * 1.25;
      return light;
    }
    vec3 toneMap(vec3 x) { return clamp((x*(2.51*x+0.03))/(x*(2.43*x+0.59)+0.14), 0.0, 1.0); }
    void main() {
      vec3 n = normalize(vNormal);
      vec3 view = normalize(vec3(0.0,0.0,4.0)-vPosition);
      if (!gl_FrontFacing) n = -n;
      // Mesh fronts/back faces carry explicit outward normals; orient toward the camera for thin contour pieces.
      n = faceforward(n, -view, n);
      vec3 r = reflect(-view, n);
      vec3 f0 = vec3(0.90, 0.65, 0.30);
      float facing = max(dot(n, view), 0.0);
      vec3 fresnel = f0 + (1.0-f0)*pow(1.0-facing,5.0);
      vec3 reflected = studio(r);
      float soft = max(dot(n,normalize(vec3(-0.4,0.7,1.0))),0.0);
      vec3 color = reflected * fresnel + f0 * (0.035 + soft * 0.035);
      gl_FragColor = vec4(pow(toneMap(color), vec3(0.4545)), 1.0);
    }`;
  class WindlustEmblem extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.canvas = this.querySelector('canvas');
      this.fallback = this.querySelector('img');
      if (!this.canvas || !this.dataset.mesh) return;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.yaw = -0.36; this.pitch = -0.10; this.targetYaw = this.yaw; this.targetPitch = this.pitch;
      this.visible = false; this.started = false; this.frame = 0; this.drag = null;
      this.playing = !this.reduced.matches && this.dataset.motion !== 'false';
      this.previousTime = 0; this.rotationMatrix = new Float32Array(9);
      this.width = 1; this.height = 1; this.resizeNeeded = true;
      this.toggle = this.querySelector('[data-emblem-toggle]');
      this.updateToggle();
      const signal = this.controller.signal;
      this.canvas.style.touchAction = 'pan-y';
      this.canvas.addEventListener('pointerdown', e => this.pointerDown(e), { signal });
      this.canvas.addEventListener('pointermove', e => this.pointerMove(e), { signal });
      this.canvas.addEventListener('pointerup', e => this.pointerEnd(e), { signal });
      this.canvas.addEventListener('pointercancel', e => this.pointerEnd(e), { signal });
      this.canvas.addEventListener('lostpointercapture', e => this.pointerEnd(e), { signal });
      this.canvas.addEventListener('keydown', e => this.keyDown(e), { signal });
      this.toggle?.addEventListener('click', () => { this.setPlaying(!this.playing); this.requestFrame(); }, { signal });
      this.canvas.addEventListener('webglcontextlost', e => { e.preventDefault(); this.fail(); }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop(); else if (this.visible && this.gl) this.requestFrame();
      }, { signal });
      this.reduced.addEventListener('change', () => {
        this.setPlaying(false); this.targetYaw = this.yaw; this.targetPitch = this.pitch; this.requestFrame();
      }, { signal });
      this.resizeObserver = new ResizeObserver(entries => {
        const rect = entries[0].contentRect, ratio = Math.min(Math.max(devicePixelRatio || 1, 1.5), 2);
        this.width = Math.max(1, Math.round(rect.width * ratio));
        this.height = Math.max(1, Math.round(rect.height * ratio));
        this.resizeNeeded = true; this.requestFrame();
      });
      this.resizeObserver.observe(this.canvas);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (!this.visible) { this.stop(); return; }
        if (!this.started) { this.started = true; this.initialize(); }
        else if (this.gl) this.requestFrame();
      }, { threshold: 0.15 });
      this.observer.observe(this);
    }
    async initialize() {
      try {
        const response = await fetch(this.dataset.mesh, { signal: this.controller.signal, cache: 'force-cache' });
        if (!response.ok) throw new Error('Emblem asset unavailable');
        const mesh = await response.json();
        if (!this.isConnected) return;
        const gl = this.canvas.getContext('webgl', { alpha: true, antialias: true, powerPreference: 'low-power', preserveDrawingBuffer: false });
        if (!gl) throw new Error('WebGL unavailable');
        this.gl = gl;
        const shader = (type, source) => {
          const value = gl.createShader(type); gl.shaderSource(value, source); gl.compileShader(value);
          if (!gl.getShaderParameter(value, gl.COMPILE_STATUS)) { gl.deleteShader(value); throw new Error('Emblem shader unavailable'); }
          return value;
        };
        const vertex = shader(gl.VERTEX_SHADER, VERTEX), fragment = shader(gl.FRAGMENT_SHADER, FRAGMENT);
        this.program = gl.createProgram(); gl.attachShader(this.program, vertex); gl.attachShader(this.program, fragment); gl.linkProgram(this.program);
        gl.deleteShader(vertex); gl.deleteShader(fragment);
        if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) throw new Error('Emblem renderer unavailable');
        gl.useProgram(this.program);
        const packed = atob(mesh.data), buffer = new ArrayBuffer(packed.length), bytes = new Uint8Array(buffer);
        for (let i = 0; i < packed.length; i++) bytes[i] = packed.charCodeAt(i);
        const source = new DataView(buffer), geometry = new Float32Array(packed.length / 2);
        for (let i = 0; i < geometry.length; i++) geometry[i] = source.getInt16(i * 2, true) / mesh.scale;
        this.count = mesh.vertices; this.indexCount = mesh.indexCount || 0;
        this.buffer = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer); gl.bufferData(gl.ARRAY_BUFFER, geometry, gl.STATIC_DRAW);
        for (const [name, offset] of [['aPosition', 0], ['aNormal', 12]]) {
          const attribute = gl.getAttribLocation(this.program, name); gl.enableVertexAttribArray(attribute); gl.vertexAttribPointer(attribute, 3, gl.FLOAT, false, 24, offset);
        }
        if (this.indexCount && mesh.indices) {
          const packedIndices = atob(mesh.indices), indexBytes = new ArrayBuffer(packedIndices.length), view = new DataView(indexBytes);
          for (let i = 0; i < packedIndices.length; i++) view.setUint8(i, packedIndices.charCodeAt(i));
          const indices = new Uint16Array(this.indexCount);
          for (let i = 0; i < this.indexCount; i++) indices[i] = view.getUint16(i * 2, true);
          this.indexBuffer = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
        }
        this.rotation = gl.getUniformLocation(this.program, 'uRotation'); this.aspect = gl.getUniformLocation(this.program, 'uAspect');
        gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LEQUAL); gl.clearColor(0, 0, 0, 0);
        this.dataset.ready = 'true';
        if (this.fallback) this.fallback.style.visibility = 'hidden';
        this.canvas.style.visibility = 'visible';
        this.requestFrame();
      } catch (error) { if (error.name !== 'AbortError') this.fail(); }
    }
    updateToggle() {
      this.dataset.paused = String(!this.playing);
      if (!this.toggle) return;
      const label = this.playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay;
      this.toggle.removeAttribute('aria-pressed');
      if (label) { (this.toggle.querySelector('[data-emblem-toggle-label]') || this.toggle).textContent = label; this.toggle.setAttribute('aria-label', label); }
    }
    setPlaying(playing) {
      this.playing = playing; this.previousTime = 0; this.updateToggle();
    }
    fail() {
      this.stop(); this.setPlaying(false); this.gl = null; this.dataset.ready = 'false';
      if (this.toggle) this.toggle.hidden = true;
      if (this.fallback) this.fallback.style.visibility = 'visible';
      if (this.canvas) { this.canvas.style.visibility = 'hidden'; this.canvas.tabIndex = -1; }
    }
    pointerDown(e) {
      if (!this.gl || e.isPrimary === false || (e.pointerType === 'mouse' && e.button !== 0)) return;
      if (e.pointerType === 'mouse') this.setPlaying(false);
      this.targetYaw = this.yaw; this.targetPitch = this.pitch;
      this.drag = { id: e.pointerId, x: e.clientX, y: e.clientY, yaw: this.yaw, pitch: this.pitch, active: e.pointerType === 'mouse' };
      if (this.drag.active) this.canvas.setPointerCapture(e.pointerId);
    }
    pointerMove(e) {
      const drag = this.drag; if (!drag || drag.id !== e.pointerId) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { this.drag = null; return; }
        if (Math.abs(dx) < 8) return;
        this.setPlaying(false); drag.yaw = this.yaw; drag.pitch = this.pitch;
        drag.active = true; this.canvas.setPointerCapture(e.pointerId);
      }
      this.targetYaw = drag.yaw + dx * 0.010;
      this.targetPitch = Math.max(-0.48, Math.min(0.48, drag.pitch + dy * 0.008));
      this.requestFrame();
    }
    pointerEnd(e) {
      if (!this.drag || this.drag.id !== e.pointerId) return;
      this.drag = null;
      if (this.canvas.hasPointerCapture(e.pointerId)) this.canvas.releasePointerCapture(e.pointerId);
      this.requestFrame();
    }
    keyDown(e) {
      if (!this.gl) return;
      const steps = { ArrowLeft: [-0.26, 0], ArrowRight: [0.26, 0], ArrowUp: [0, -0.12], ArrowDown: [0, 0.12] };
      if (steps[e.key]) {
        e.preventDefault(); this.setPlaying(false); this.targetYaw += steps[e.key][0];
        this.targetPitch = Math.max(-0.48, Math.min(0.48, this.targetPitch + steps[e.key][1])); this.requestFrame();
      } else if (e.key === 'Home' || e.key === 'Escape') {
        e.preventDefault(); this.setPlaying(false); this.targetYaw = -0.36; this.targetPitch = -0.10; this.requestFrame();
      } else if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault(); this.setPlaying(!this.playing); this.requestFrame();
      }
    }
    requestFrame() {
      if (!this.frame && this.gl && this.visible && !document.hidden) this.frame = requestAnimationFrame(t => this.render(t));
    }
    stop() { if (this.frame) cancelAnimationFrame(this.frame); this.frame = 0; this.previousTime = 0; }
    render(time) {
      this.frame = 0;
      if (!this.gl || !this.visible || document.hidden) return;
      const dt = this.previousTime ? Math.min((time - this.previousTime) / 1000, 0.05) : 0;
      this.previousTime = time;
      if (this.playing && (!this.drag || !this.drag.active)) {
        const step = dt * (Math.PI * 2 / 30);
        this.targetYaw += step; this.yaw += step;
      }
      const dy = this.targetYaw - this.yaw, dp = this.targetPitch - this.pitch;
      const moving = Math.abs(dy) + Math.abs(dp) > 0.0005;
      const blend = this.reduced.matches ? 1 : (1 - Math.exp(-Math.max(dt, 0.016) * 16));
      this.yaw += dy * blend; this.pitch += dp * blend;
      const gl = this.gl;
      if (this.resizeNeeded) {
        this.canvas.width = this.width; this.canvas.height = this.height;
        gl.viewport(0, 0, this.width, this.height); gl.uniform1f(this.aspect, this.width / this.height);
        this.resizeNeeded = false;
      }
      const cy = Math.cos(this.yaw), sy = Math.sin(this.yaw), cx = Math.cos(this.pitch), sx = Math.sin(this.pitch), matrix = this.rotationMatrix;
      matrix[0]=cy; matrix[1]=sx*sy; matrix[2]=-cx*sy;
      matrix[3]=0; matrix[4]=cx; matrix[5]=sx;
      matrix[6]=sy; matrix[7]=-sx*cy; matrix[8]=cx*cy;
      gl.uniformMatrix3fv(this.rotation, false, matrix);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      if (this.indexCount) gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_SHORT, 0);
      else gl.drawArrays(gl.TRIANGLES, 0, this.count);
      if (this.playing || moving) this.requestFrame(); else this.previousTime = 0;
    }
    disconnectedCallback() {
      this.stop(); this.controller?.abort(); this.observer?.disconnect(); this.resizeObserver?.disconnect();
      if (this.gl) { this.gl.deleteBuffer(this.buffer); if (this.indexBuffer) this.gl.deleteBuffer(this.indexBuffer); this.gl.deleteProgram(this.program); }
      this.gl = null; this.controller = null;
    }
  }
  customElements.define('windlust-emblem', WindlustEmblem);
})();
